// pages/dashboard/finance/VatOnSalesDialog.tsx
// ─────────────────────────────────────────────────────────────────────────────
// VAT100 Box 1 drill-down: VAT on the in-app sales for the quarter and every
// item sold, each opening its product-level VAT breakdown.
//
// Box 1 is the practice's whole output VAT. Besides the in-app sales listed
// here it includes standard-rated income recorded on the bank feed, so the
// dialog shows both sources rather than implying the item list adds up to Box 1.

import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { ISalesSummary } from '../../../@types/dispense';
import { IVatIncomeSources } from '../../../@types/finance';
import { DispenseService } from '../../../api/dispense.service';
import ItemsSoldVatTable from '../../../components/dispense/ItemsSoldVatTable';
import Iconify from '../../../components/iconify';
import { fCurrency } from '../../../utils/formatNumber';
import { pluralize } from '../../../utils/pluralize';

const dispenseService = new DispenseService();

function fMoney(value: number | null | undefined) {
  return value ? fCurrency(value) : '£0.00';
}

type Props = {
  open: boolean;
  onClose: () => void;
  /** Inclusive YYYY-MM-DD bounds of the quarter. */
  dateFrom: string;
  dateTo: string;
  quarterLabel: string;
  box1: number;
  incomeSources?: IVatIncomeSources;
};

function Figure({
  label,
  value,
  caption,
  loading,
}: {
  label: string;
  value: string;
  caption?: string;
  loading?: boolean;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={100} height={36} />
      ) : (
        <Typography variant="h5">{value}</Typography>
      )}
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Paper>
  );
}

export default function VatOnSalesDialog({
  open,
  onClose,
  dateFrom,
  dateTo,
  quarterLabel,
  box1,
  incomeSources,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [summary, setSummary] = useState<ISalesSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setSummary(null);
    dispenseService
      .getSalesSummary({ dateFrom, dateTo })
      .then((res) => {
        if (!cancelled) setSummary(res.data);
      })
      .catch((error: { message?: string }) => {
        if (!cancelled) {
          enqueueSnackbar(error?.message || 'Could not load VAT on sales', {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, dateFrom, dateTo, enqueueSnackbar]);

  const bankGrossS = incomeSources?.bankGrossS ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Box 1 — VAT due on sales
        <Typography variant="caption" color="text.secondary" display="block">
          {quarterLabel} · items sold and the VAT charged on each sale
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Figure
              label="Box 1 (return)"
              value={fMoney(box1)}
              caption="All standard-rated output VAT"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Figure
              label="VAT on in-app sales"
              value={fMoney(summary?.sales.vatAmount)}
              caption={`Exempt ${fMoney(summary?.sales.exemptAmount)}`}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Figure
              label="In-app sales (gross)"
              value={fMoney(summary?.sales.grossSales)}
              caption={pluralize(summary?.sales.saleCount, 'sale')}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Figure
              label="Bank-recorded sales (S)"
              value={fMoney(bankGrossS)}
              caption="Gross, from the bank feed"
            />
          </Grid>
        </Grid>

        {bankGrossS > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Box 1 also includes VAT on standard-rated income recorded on the
            bank feed, so it is higher than the VAT on the items below.
          </Alert>
        )}

        <Box>
          <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
            Items sold
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Open a row to see the full product-level VAT breakdown for that
            sale.
          </Typography>
          <ItemsSoldVatTable dateFrom={dateFrom} dateTo={dateTo} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
